import React, { useMemo, useState, useEffect } from 'react'
import { Box, Menu, MenuItem, Typography } from '@mui/material'
import { useTheme } from '@mui/system'
import { TbTargetArrow } from 'react-icons/tb'
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined'
import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined'

import DotAdsDasIcon from '../../../assets/DotAdsDasIcon.svg'
import { HiOutlineSpeakerphone } from 'react-icons/hi'
import { FaPaintBrush } from 'react-icons/fa'

export default function PlatformSwitchSection() {
  const theme = useTheme()

  const platformsLists = useMemo(
    () => [
      {
        id: 1,
        name: 'DOT Ads',
        description: 'Access 175 million users',
        icon: <Box component="img" src={DotAdsDasIcon} width={24} height={24} />,
        link: null
      },
      {
        id: 2,
        name: 'Meta Ads',
        description: 'Unlimited access to Instagram & Facebook',
        link: 'https://dotaudiences.com/metaads/',
        icon: <TbTargetArrow size={22} />
      },
      {
        id: 3,
        name: 'PR Newswire',
        description: 'Articles Published on 15,000 Top Sites',
        link: 'https://newswire.dotaudiences.com/',
        icon: <HiOutlineSpeakerphone size={22} />
      },
      {
        id: 4,
        name: 'Creative Design Service',
        description: 'Captivating banners that convert',
        link: 'https://dotaudiences.com/creativepricing',
        icon: <FaPaintBrush size={20} />
      }
    ],
    []
  )

  const [selectedPlatform, setSelectedPlatform] = useState(platformsLists[0])

  useEffect(() => {
      setSelectedPlatform(platformsLists[0])
  }, [platformsLists])

  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSelectPlatform = (platform: any) => {
    handleClose()
    if (platform.link) window.open(platform.link, '_blank')
  }

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          mt: 1,
          background: '#FFFEEF',
          minWidth: '100%',
          borderRadius: '8px',
          padding: '8px 12px',
          color: '#000000',
          alignItems: 'center',
          display: 'flex',
          fontFamily: 'Inter-SemiBold !important',
          lineHeight: '32px',
          gap: 1,
          cursor: 'pointer',
          justifyContent: 'space-between',
          transition: 'background 0.2s ease',
          '&:hover': {
            background: '#FFFAD1'
          }
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              background: theme.palette.primary.main,
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'center',
              color: '#fff'
            }}
          >
            {selectedPlatform.icon}
          </Box>
          <Typography fontWeight="600" fontSize={'24px'} letterSpacing={'-0.05em'}>
            {selectedPlatform.id === 4 ? 'Design Service' : selectedPlatform.name}
          </Typography>
        </Box>
        {open ? <ExpandLessOutlinedIcon /> : <ExpandMoreOutlinedIcon />}
      </Box>

      <Menu
        anchorEl={anchorEl}
        id="platform-switch-menu"
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 3,
          sx: {
            overflow: 'visible',
            mt: 1.5,
            borderRadius: '20px',
            width: 260,
            p: 1,
            '&::before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0
            }
          }
        }}
      >
        {platformsLists.map((platform, index) => {
          const isSelected = selectedPlatform.name === platform.name
          return (
            <MenuItem
              key={index}
              onClick={() => handleSelectPlatform(platform)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 1.2,
                borderRadius: '8px',
                mb: 0.5,
                backgroundColor: isSelected ? `${theme.palette.primary.main} !important` : 'transparent',
                color: isSelected ? '#FFFFFF' : theme.palette.text.primary,
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                  color: '#FFFFFF',
                  '& .icon-box': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'rgba(255 255 255)'
                  },
                  '& .text-box': {
                    color: 'rgba(255 255 255)'
                  }
                }
              }}
            >
              <Box
                className="icon-box"
                sx={{
                  backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.1) !important' : theme.palette.violet?.[50],
                  color: isSelected ? '#FFFFFF' : theme.palette.primary.main,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '36px',
                  height: '36px',
                  flexShrink: 0,
                  transition: 'all 0.2s ease'
                }}
              >
                {platform.icon}
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography
                  className="text-box"
                  sx={{
                    fontWeight: 600,
                    fontSize: '14px',
                    color: isSelected ? '#FFFFFF !important' : theme.palette.text.primary,
                    wordBreak: 'break-word'
                  }}
                >
                  {platform.name}
                </Typography>
                <Typography
                  className="text-box"
                  sx={{
                    fontSize: '14px',
                    color: isSelected ? 'rgba(255,255,255,0.8) !important' : theme.palette.text.secondary,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word'
                  }}
                >
                  {platform.description}
                </Typography>
              </Box>
            </MenuItem>
          )
        })}
      </Menu>
    </>
  )
}
